import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _dec, _class, _Distribution;
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
/** @jsx jsx */
import { Component } from 'react';
import { PropTypes } from 'prop-types';
import { max, range, sum } from 'd3-array';
import { axisBottom } from 'd3-axis';
import { scaleBand, scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { jsx, css } from '@instructure/emotion';
import t from '@instructure/quiz-i18n/es/format-message';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { ScreenReaderContent, PresentationContent } from '@instructure/ui-a11y-content';
import { transition } from 'd3-transition';
import flow from 'lodash/flow';
import { withStyleOverrides } from '@instructure/quiz-common';

// Star path copied from instructure-icons/svg/Solid/star.svg
export var starPathData = 'M1915.918 737.475c-10.955-33.543-42.014-56.131-77.364-56.131h-612.029l-189.063-582.1v-.112C1026.394 65.588 995.335 43 959.984 43c-35.237 0-66.41 22.588-77.365 56.245L693.443 681.344H81.415c-35.35 0-66.41 22.588-77.365 56.131-10.955 33.544.79 70.137 29.478 91.03l495.247 359.831-189.177 582.212c-10.955 33.657 1.13 70.25 29.817 90.918 14.23 10.278 30.946 15.473 47.66 15.473 16.716 0 33.432-5.195 47.775-15.586l495.134-359.718 495.021 359.718c28.574 20.781 67.087 20.781 95.662.113 28.687-20.668 40.658-57.261 29.703-91.03l-189.176-582.1 495.36-359.83c28.574-20.894 40.433-57.487 29.364-91.03';
var MARGIN_T = 0;
var MARGIN_B = 28;
export var Distribution = (_dec = withStyleOverrides(generateStyle, generateComponentTheme), _dec(_class = (_Distribution = /*#__PURE__*/function (_Component) {
  function Distribution() {
    var _this;
    _classCallCheck(this, Distribution);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _callSuper(this, Distribution, [].concat(args));
    _defineProperty(_this, "_isStarred", function (d, i) {
      return typeof _this.props.starIndex === 'number' && i === _this.props.starIndex;
    });
    _defineProperty(_this, "_renderStar", function (svg) {
      if (typeof _this.props.starIndex !== 'number') return;
      var star = svg.append('path').attr('d', starPathData).attr('style', css(_this.props.styles.barStar).styles);
      var starIndex = _this.props.starIndex;
      var starX = _this.x(starIndex) + _this.x.bandwidth() / 2;
      var starY = _this.y(_this.props.visualDistribution[starIndex]) - 30;
      // scale the star down and position it on the x axis - y axis will be applied by animation
      var xTransform = "translate(".concat(starX, " 0) scale(0.011) translate(-960 0)");
      _this.applyTransition(star, function (rects) {
        return rects.attr('opacity', 0).attr('transform', "translate(0 ".concat(starY + 20, ") ").concat(xTransform));
      }, function (rects) {
        return rects.attr('opacity', 1).attr('transform', "translate(0 ".concat(starY, ") ").concat(xTransform));
      }, function (transition) {
        return transition.delay(_this.props.animateDuration).duration(600);
      });
    });
    _defineProperty(_this, "applyTransition", function (selection, initialFunc, finalFunc, transitionFunc) {
      var identity = function identity(s) {
        return s;
      };
      var transition = function transition(s) {
        return s.transition(_this.getTransition());
      };
      var applicators = [];
      if (_this.isAnimated()) {
        applicators.push(initialFunc || identity, transition, transitionFunc || identity);
      }
      applicators.push(finalFunc || identity);
      return flow(applicators)(selection);
    });
    _defineProperty(_this, "setChartRef", function (ref) {
      _this.chart = ref;
    });
    return _this;
  }
  _inherits(Distribution, _Component);
  return _createClass(Distribution, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.__svg = this.createChart(this.chart, this.props);
      this.updateChart(this.__svg, this.props);
    }
  }, {
    key: "shouldComponentUpdate",
    value: function shouldComponentUpdate(nextProps) {
      this.updateChart(this.__svg, nextProps);
      return false;
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.removeChart();
    }
  }, {
    key: "isAnimated",
    value: function isAnimated() {
      return this.props.animateDelay || this.props.animateDuration;
    }
  }, {
    key: "calculateAxis",
    value: function calculateAxis(props) {
      // the x scale is static since it will always represent the BucketCount
      // percentiles, so we can avoid recalculating it on every update:
      this.x = scaleBand().rangeRound([0, this.width]).paddingInner(props.barPaddingInner).paddingOuter(props.barPaddingOuter).domain(range(props.visualDistribution.length));
      var highest = max(props.visualDistribution);
      this.y = scaleLinear().rangeRound([this.height, props.barPaddingTop]).domain([0, highest]);
      this.xAxis = axisBottom(this.x).tickSize([0]).tickValues(range(props.visualDistribution.length));
      this.props.calculateAxis(this.x, this.y, this.xAxis, this.height);
    }
  }, {
    key: "weightedAvg",
    value: function weightedAvg(data) {
      var partitions = data.length - 1;
      var totalStudents = sum(data);
      var avg = data.reduce(function (total, count, index) {
        // Assumes even weight of scores across distribution
        var score = index / partitions;
        return total + score * count;
      }, 0.0) / totalStudents;
      var avgPartition = Math.floor(avg * partitions);
      return {
        avg: avg,
        aboveAvg: sum(data.slice(avgPartition)),
        belowAvg: sum(data.slice(0, avgPartition))
      };
    }
  }, {
    key: "summarizeChart",
    value: function summarizeChart() {
      var data = this.props.summaryDistribution || this.props.visualDistribution;
      if (data.length <= 1) {
        return '';
      }
      var avgInfo = this.weightedAvg(data);
      if (avgInfo.belowAvg + avgInfo.aboveAvg < 1) {
        return '';
      }
      var textForScreenreaders = t('{aboveAvg} students scored above or at the average, and {belowAvg} below. ', {
        aboveAvg: avgInfo.aboveAvg,
        belowAvg: avgInfo.belowAvg
      });
      data.forEach(function (datum, i) {
        if (datum > 0) {
          textForScreenreaders += t("{\n            count, plural,\n            one {# student scored between {lower, number, percent} and {upper, number, percent}. }\n            other {# students scored between {lower, number, percent} and {upper, number, percent}. }\n          }", {
            count: datum,
            lower: i / data.length,
            upper: (i + 1) / data.length
          });
        }
      });
      return this.props.summarizeChart(textForScreenreaders);
    }
  }, {
    key: "createChart",
    value: function createChart(node, props) {
      // parentNode for IE11
      var parentWidth = (node.parentElement || node.parentNode || {}).offsetWidth;
      this.width = props.chartWidth || parentWidth;
      this.height = props.chartHeight;
      this.viewportHeight = this.height + MARGIN_T + MARGIN_B;
      this.calculateAxis(props);
      var svg = select(node).attr('role', 'document').attr('aria-role', 'document').attr('width', '100%').attr('height', '100%').attr('viewBox', "0 0 ".concat(this.width, " ").concat(this.viewportHeight)).attr('preserveAspectRatio', 'xMidYMax').append('g');
      this.title = Distribution.addTitle(svg, t('Score percentiles chart'));
      svg.append('g').attr('class', 'xAxis').attr('style', css(this.props.styles.xAxis).styles).attr('data-role', 'xAxis').attr('aria-hidden', true).attr('transform', "translate(0,".concat(this.height, ")")).call(this.xAxis).selectAll('text').attr('class', 'barLabel').attr('style', css(this.props.styles.barLabel).styles).attr('y', 9);
      var clipKey = "clipPath".concat(this.width, "x").concat(this.height);
      var clipPath = svg.append('clipPath').attr('id', clipKey);
      clipPath.append('rect').attr('x', 0).attr('y', 0).attr('width', this.width).attr('height', this.height);
      this.barContainer = svg.append('g').attr('class', 'barContainer').attr('clip-path', "url(#".concat(clipKey, ")"));
      this.props.createChart(svg, this.width);
      return svg;
    }
  }, {
    key: "updateChart",
    value: function updateChart(svg, props) {
      this.renderBars(this.barContainer, props);
      this._renderStar(this.barContainer);
      this.props.updateChart(svg, this.x, this.y, this.applyTransition, this.barContainer, this.height);
    }
  }, {
    key: "removeChart",
    value: function removeChart() {
      if (this.__svg) {
        this.__svg.remove();
        delete this.__svg;
      }
    }
  }, {
    key: "renderBars",
    value: function renderBars(node, props) {
      var _this2 = this;
      this.chartData = props.visualDistribution;
      var height = this.height;
      var x = this.x;
      var y = this.y;
      var bars = node.selectAll('rect').data(this.chartData);
      var rects = bars.enter().append('rect').attr('style', function (d, i) {
        return _this2._isStarred(d, i) ? css(_this2.props.styles.barStar).styles : css(_this2.props.styles.bar).styles;
      }).attr('class', function (d, i) {
        return _this2._isStarred(d, i) ? 'barStar' : 'bar';
      }).attr('x', function (d, i) {
        return x(i);
      }).attr('width', x.bandwidth());
      this.applyTransition(rects, function (rects) {
        return rects.attr('y', height).attr('height', 0);
      }, function (rects) {
        return rects.attr('y', y).attr('height', function (d) {
          return height - y(d);
        });
      });
      bars.exit().remove();
    }
  }, {
    key: "getTransition",
    value: function getTransition() {
      this.transition = this.transition || transition().delay(this.props.animateDelay || 0).duration(this.props.animateDuration || 0);
      return this.transition;
    }
  }, {
    key: "renderSummary",
    value: function renderSummary() {
      return jsx(ScreenReaderContent, null, this.summarizeChart());
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props = this.props,
        chartWidth = _this$props.chartWidth,
        chartHeight = _this$props.chartHeight;
      var viewportHeight = chartHeight + MARGIN_T + MARGIN_B;

      // IE11 sizes svg elements incorrectly, so we render a canvas element to
      // layout space for the chart, then position the svg absolute on top of it.
      return jsx("div", null, jsx(PresentationContent, {
        as: "div"
      }, jsx("div", {
        style: {
          position: 'relative'
        }
      }, jsx("canvas", {
        width: chartWidth,
        height: viewportHeight,
        style: {
          display: 'block',
          visibility: 'hidden'
        }
      }), jsx("svg", {
        ref: this.setChartRef,
        style: {
          position: 'absolute',
          top: 0,
          left: 0
        }
      }))), this.renderSummary(), this.props.renderTable());
    }
  }], [{
    key: "addTitle",
    value: function addTitle(svg, title) {
      return svg.append('title').text(title);
    }
  }]);
}(Component), _defineProperty(_Distribution, "displayName", 'Distribution'), _defineProperty(_Distribution, "componentId", "Quizzes".concat(_Distribution.displayName)), _defineProperty(_Distribution, "propTypes", {
  animateDelay: PropTypes.number,
  animateDuration: PropTypes.number,
  // ESLint thinks these props aren't used but they are via a call chain in componentDidMount
  barPaddingInner: PropTypes.number,
  // Spacing between bars, so each is visible, could be accomplished by borders
  barPaddingOuter: PropTypes.number,
  // Spacing between bars, so each is visible, could be accomplished by borders
  barPaddingTop: PropTypes.number,
  chartHeight: PropTypes.number.isRequired,
  // Height in pixels
  chartWidth: PropTypes.number,
  /* *
   * * a fallback minimal distribution for tab-able, keyboard navigable buckets
   * * typically this will be ~ 10 buckets
   * */
  summaryDistribution: PropTypes.arrayOf(PropTypes.node),
  /* *
   * * A list of score values for each bucket.  Size of this array determines the number of buckets
   * */
  visualDistribution: PropTypes.arrayOf(PropTypes.node).isRequired,
  calculateAxis: PropTypes.func,
  createChart: PropTypes.func,
  renderTable: PropTypes.func,
  summarizeChart: PropTypes.func,
  updateChart: PropTypes.func,
  starIndex: PropTypes.number,
  styles: PropTypes.object
}), _defineProperty(_Distribution, "defaultProps", {
  animateDelay: 200,
  animateDuration: 900,
  barPaddingInner: 0.25,
  // Spacing between bars, so each is visible, could be accomplished by borders
  barPaddingOuter: 0,
  // Spacing between bars, so each is visible, could be accomplished by borders
  barPaddingTop: 0,
  // Spacing between highest bar and top of chart, pixels
  chartHeight: 200,
  // Height in pixels
  chartWidth: null,
  summaryDistribution: void 0,
  visualDistribution: [],
  calculateAxis: Function.prototype,
  createChart: Function.prototype,
  renderTable: Function.prototype,
  summarizeChart: function summarizeChart(textForScreenreaders) {
    return textForScreenreaders;
  },
  updateChart: Function.prototype,
  starIndex: null
}), _Distribution)) || _class);
/**
 *   A component to render an a11y friendly distribution of scores across users.
 *   Underneath the chart is two accessible features:
 *     1. A keyboard friendly set of min(10, bucketCounts) buckets {0-10%, 10-20%, ..., 90-100%}
 *     with the number of users in that slice
 *     2. A screenreader-only block of content providing a table version of all the data
 *
 *     ```jsx_example
 *      <Distribution
 *        summaryDistribution={Array.from({length: 10}, () => Math.floor(Math.random() * 5))}
 *        visualDistribution={Array.from({length: 101}, () => Math.floor(Math.random() * 5))}
 *      />
 *      ```
 *  **/
export default Distribution;